import * as React from 'react';
import { cn } from '@utils';

// Type definitions for component props
export type DashedLineOrientation = 'horizontal' | 'vertical';
export type DashedLineVariant = 'default' | 'muted' | 'primary' | 'secondary' | 'accent';
export type DashedLineSize = 'thin' | 'default' | 'thick';
export type DashedLineDashLength = 'short' | 'default' | 'long';

export interface DashedLineProps extends React.HTMLAttributes<HTMLDivElement> {
	/**
	 * The orientation of the dashed line
	 * @default 'horizontal'
	 */
	orientation?: DashedLineOrientation;
	
	/**
	 * The color variant of the dashed line
	 * @default 'default'
	 */
	variant?: DashedLineVariant;
	
	/**
	 * The size/thickness of the dashed line
	 * @default 'default'
	 */
	size?: DashedLineSize;
	
	/**
	 * The length of the dash pattern
	 * @default 'default'
	 */
	dashLength?: DashedLineDashLength;
	
	/**
	 * Custom CSS class name
	 */
	className?: string;
}

const DashedLine = React.forwardRef<HTMLDivElement, DashedLineProps>(
	({ 
		orientation = 'horizontal', 
		variant = 'default', 
		size = 'default', 
		dashLength = 'default',
		className,
		...props 
	}, ref) => {
		const baseClasses = 'border-dashed';
		
		const orientationClasses = {
			horizontal: 'w-full border-t',
			vertical: 'h-full border-l'
		};
		
		const variantClasses = {
			default: 'border-gray-300',
			muted: 'border-gray-200',
			primary: 'border-primary',
			secondary: 'border-secondary',
			accent: 'border-accent'
		};
		
		const sizeClasses = {
			thin: orientation === 'horizontal' ? 'border-t' : 'border-l',
			default: orientation === 'horizontal' ? 'border-t-[2px]' : 'border-l-[2px]',
			thick: orientation === 'horizontal' ? 'border-t-[3px]' : 'border-l-[3px]'
		};
		
		const dashLengthClasses = {
			short: 'border-dashed',
			default: 'border-dashed',
			long: 'border-dashed'
		};
		
		// Custom dash pattern using CSS
		const customDashStyles = {
			short: { borderStyle: 'dashed', borderDasharray: '4,4' },
			default: { borderStyle: 'dashed', borderDasharray: '8,4' },
			long: { borderStyle: 'dashed', borderDasharray: '12,6' }
		};
		
		return (
			<div
				ref={ref}
				className={cn(
					baseClasses,
					orientationClasses[orientation],
					variantClasses[variant],
					sizeClasses[size],
					dashLengthClasses[dashLength],
					className
				)}
				style={customDashStyles[dashLength]}
				data-aid="dashed-line"
				{...props}
			/>
		);
	}
);

DashedLine.displayName = 'DashedLine';

export { DashedLine }; 
